import React, { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Box, FlexBox, Heading } from "@braze/beacon-core";
import { getSpacing } from "@braze/beacon-styling";
import { yupResolver } from "@braze/hookform-resolvers-legacy/yup";
import * as Yup from "@braze/yup-legacy";

import useI18nNamespace from "@services/useI18nNamespace";

import { workEmailSchema } from "../WorkEmailInputField";
import { phoneNumberCountryCodeSchema, phoneNumberSchema } from "../utils";
import { EmailVerification } from "./EmailVerification";
import { SmsVerification } from "./SmsVerification";
import { StyledBodyText, StyledHeading } from "./VerifyContactDetails.style";
import type { VerifyContactDetailsProps, VerifyContactDetailsType } from "./types";

export const VerifyContactDetails = ({
  contactDetails,
  onSubmit,
  setShowSalesBanner,
}: VerifyContactDetailsProps): React.ReactElement => {
  const { t } = useI18nNamespace("free-trial-registration");
  const validationSchema = useMemo(
    () =>
      Yup.object({
        email: workEmailSchema(t),
        phoneNumber: phoneNumberSchema(t),
        phoneNumberCountry: phoneNumberCountryCodeSchema(t),
      }),
    [t]
  );
  const form = useForm<VerifyContactDetailsType>({
    defaultValues: contactDetails,
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
  });

  const { handleSubmit } = form;
  const [emailVerified, setEmailVerified] = useState<boolean>(false);
  const handleVerificationComplete = () => {
    setEmailVerified(true);
  };

  return (
    <FlexBox direction="column" id="verifyContactDetailsForm" data-cy="verifyContactDetailsForm">
      <FlexBox direction="column" alignItems="center" gap={getSpacing("xl") as unknown as string} my="lg">
        <StyledHeading data-cy="verifyContactDetailsFormHeading" level={2}>
          {t("verification-heading", "Almost there! Verify that it's you.")}
        </StyledHeading>
        <StyledBodyText data-cy="verifyContactDetailsFormInfoText">
          {t(
            "verification-description",
            "To keep your trial account secure and prevent abuse, please verify your work email and phone number. Your data is used for verification only and stays private."
          )}
        </StyledBodyText>
      </FlexBox>
      <FormProvider {...form}>
        <Box mt="lg">
          <Heading data-cy="verifyContactDetailsFormSubHeading" level={4}>
            {t("verification-text", "Two-step verification")}
          </Heading>
        </Box>
        <EmailVerification
          emailVerified={emailVerified}
          onVerificationComplete={handleVerificationComplete}
          setShowSalesBanner={setShowSalesBanner}
        />
        {emailVerified && (
          <SmsVerification
            onVerificationComplete={handleSubmit(onSubmit)}
            setShowSalesBanner={setShowSalesBanner}
          />
        )}
      </FormProvider>
    </FlexBox>
  );
};
